import axiosConn from '../../axiosConn';
import { useMediaQuery } from 'react-responsive';
import { CircularProgress } from '@material-ui/core';
import { Tooltip } from '@mui/material';
import { useEffect, useState, useRef, useMemo } from 'react';
import './conversation.css';
import { snakeAudio, dreamerAudio } from '../../context/socket';
import ContextMenu from './contextMenu/ContextMenu';
import useClickOutside from '../../clickOutside';
import getMobileOS from '../../utils/getMobileOs';
import useUIEffects from '../../utils/useUIEffects';
import { useTheme } from '../../context/ThemeContext';
import formatLastSeen from '../../utils/presence/formatLastSeen';

const initialContextMenu = { show: false, x: 0, y: 0 };

export default function Conversation({
  onlinePlayers,
  presenceMap,
  conversation,
  currentPlayer,
  setMessages,
  setCurrentChat,
  currentChat,
  arrivalMessage,
  isOnline: isOnlineProp,
  onParticipantResolved,
}) {
  const [player, setPlayer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fwiendId, setFwiendId] = useState(null);
  const [contextMenu, setContextMenu] = useState(initialContextMenu);
  const [open, setOpen] = useState(false);
  const [showUnReadMessages, setShowUnReadMessages] = useState(false);
  const [opacity, setOpacity] = useState(1);

  const os = getMobileOS();
  const mobileView = useMediaQuery({ query: '(max-width: 805px)' });
  const { darkTheme } = useTheme();
  const contextmenu = useRef(null);
  useClickOutside(contextmenu, () => setContextMenu(initialContextMenu));
  const { handleContextMenuUI, closeContextMenu, handleTooltip, fadeOut } =
    useUIEffects();

  // Fetch participant profile
  useEffect(() => {
    let mounted = true;
    const friendId = conversation.members.find(
      (m) => m !== currentPlayer.data.player._id,
    );
    setFwiendId(friendId);
    const getPlayer = async () => {
      const looksLikeObjectId =
        typeof friendId === 'string' && /^[0-9a-f]{24}$/i.test(friendId);
      if (!looksLikeObjectId) {
        console.error('[conversation] Invalid member id; skipping fetch', {
          conversationId: conversation?._id,
          memberValue: friendId,
        });
      }
      try {
        setIsLoading(true);
        const res = await axiosConn(`players/${friendId}`, {
          headers: { Authorization: `Bearer ${currentPlayer.token}` },
        });
        if (!mounted) return;
        const nextPlayer = res.data.data.data;
        setPlayer(nextPlayer);
        if (onParticipantResolved) {
          onParticipantResolved(friendId, nextPlayer);
        }
      } catch (err) {
        // Debug: surface problematic conversations (e.g., non-ObjectId member)
        // Do not block UI; just log.
        console.warn('[conversation] failed to load participant', {
          conversationId: conversation?._id,
          friendId,
          error: err?.message || err,
        });
      } finally {
        mounted && setIsLoading(false);
      }
    };
    if (friendId) getPlayer();
    return () => {
      mounted = false;
    };
  }, [
    conversation.members,
    currentPlayer.data.player._id,
    currentPlayer.token,
    onParticipantResolved,
  ]);

  // Unread badge
  useEffect(() => {
    setShowUnReadMessages(conversation.unReadMessages > 0);
  }, [conversation.unReadMessages]);

  // Arrival message -> unread indicator
  useEffect(() => {
    if (
      arrivalMessage &&
      conversation?.members.includes(arrivalMessage.sender) &&
      Date.now() - arrivalMessage.createdAt < 1000 &&
      conversation?._id !== currentChat?._id
    )
      setShowUnReadMessages(true);
  }, [
    arrivalMessage,
    conversation?._id,
    conversation?.members,
    currentChat?._id,
  ]);

  // Presence resolution (multi source + status tiers)
  const presenceEntry = useMemo(() => {
    if (!presenceMap) return null;
    const keys = [
      fwiendId,
      player?._id,
      player?.mongoId,
      player?.accountId,
      player?.playerId,
      player?.handle,
    ]
      .filter(Boolean)
      .map((val) => String(val));
    for (const key of keys) {
      const direct = presenceMap.get(key);
      if (direct) return direct;
    }
    for (const [, val] of presenceMap) {
      const candidates = [
        val?._id,
        val?.playerId,
        val?.mongoId,
        val?.accountId,
        val?.handle,
        val?.id,
        val?.source?._id,
        val?.source?.playerId,
      ]
        .filter(Boolean)
        .map((item) => String(item));
      if (candidates.some((id) => keys.includes(id))) {
        return val;
      }
    }
    return null;
  }, [fwiendId, player, presenceMap]);

  const resolvedLastSeen = useMemo(() => {
    if (!presenceEntry) return null;
    return (
      presenceEntry.lastSeenAt ??
      presenceEntry.lastSeen ??
      presenceEntry.lastUpdatedAt ??
      presenceEntry.timestamp ??
      presenceEntry.updatedAt ??
      presenceEntry.wentOfflineAt ??
      null
    );
  }, [presenceEntry]);

  const computedOnline = useMemo(() => {
    if (presenceEntry?.status) return presenceEntry.status !== 'offline';
    if (presenceEntry && typeof presenceEntry.online === 'boolean') {
      return !!presenceEntry.online;
    }
    if (typeof isOnlineProp === 'boolean') return isOnlineProp;
    return onlinePlayers?.includes(fwiendId);
  }, [isOnlineProp, presenceEntry, onlinePlayers, fwiendId]);

  // Derive richer status (idle / away) from explicit status or lastSeen recency
  const derivedStatus = useMemo(() => {
    if (!presenceEntry) return computedOnline ? 'online' : 'offline';
    const explicitRaw =
      presenceEntry.status || presenceEntry.presenceStatus || '';
    const explicit = String(explicitRaw).toLowerCase();
    if (explicit === 'online' || explicit === 'active' || explicit === 'available') {
      return 'online';
    }
    if (explicit === 'idle' || explicit === 'away' || explicit === 'background') {
      return explicit === 'background' ? 'idle' : explicit;
    }
    if (explicit === 'offline') return 'offline';
    const now = Date.now();
    const ls = resolvedLastSeen || now;
    const delta = now - ls;
    const onlineFlag =
      typeof presenceEntry.online === 'boolean'
        ? presenceEntry.online
        : computedOnline;
    if (!onlineFlag) return 'offline';
    if (delta < 60_000) return 'online';
    if (delta < 5 * 60_000) return 'idle';
    if (delta < 15 * 60_000) return 'away';
    return 'offline';
  }, [presenceEntry, resolvedLastSeen, computedOnline]);

  const statusClass = `presenceDot presence-${derivedStatus}`;
  const lastSeenLabel = useMemo(
    () => (resolvedLastSeen ? formatLastSeen(resolvedLastSeen) : null),
    [resolvedLastSeen],
  );
  const lastSeenSuffix =
    lastSeenLabel && lastSeenLabel !== 'unknown' ? ` · ${lastSeenLabel}` : '';
  const statusTextDisplay =
    derivedStatus === 'online'
      ? `Online${lastSeenSuffix}`
      : derivedStatus === 'idle'
      ? `Idle${lastSeenSuffix}`
      : derivedStatus === 'away'
      ? `Away${lastSeenSuffix}`
      : `Offline${lastSeenSuffix}`;

  const active = currentChat?._id === conversation._id;

  // === Presence audio: only play when crossing offline <-> (online|idle|away) boundary ===
  const lastPresenceGroupRef = useRef(null);
  useEffect(() => {
    if (!fwiendId) return;
    const group = derivedStatus === 'offline' ? 'offline' : 'online';
    if (lastPresenceGroupRef.current == null) {
      lastPresenceGroupRef.current = group;
      return;
    }
    // Only play audio if this conversation is active
    if (lastPresenceGroupRef.current !== group && active) {
      if (group === 'online') {
        snakeAudio.currentTime = 0;
        snakeAudio.play().catch(() => {});
      } else {
        dreamerAudio.currentTime = 0;
        dreamerAudio.play().catch(() => {});
      }
      lastPresenceGroupRef.current = group;
    } else if (lastPresenceGroupRef.current !== group) {
      lastPresenceGroupRef.current = group;
    }
  }, [derivedStatus, fwiendId, active]);

  return (
    <div
      className={`conversationItem ${
        darkTheme ? 'conversationDark' : 'conversationLight'
      } ${active ? 'conversationActive' : ''}`}
      aria-label={`Conversation with ${
        player?.playerId || 'player'
      } status ${derivedStatus}`}
      data-status={derivedStatus}
    >
      {isLoading ? (
        <div style={{ padding: 8 }}>
          <CircularProgress color="inherit" size="14px" />
        </div>
      ) : (
        <Tooltip
          open={open}
          onClose={() => handleTooltip(setOpen).close()}
          onOpen={() => handleTooltip(setOpen).open()}
          title={`Right click for ${player?.playerId} options`}
          placement="right"
        >
          <div
            ref={contextmenu}
            className={`conversationCore ${contextMenu.show ? 'ctx-open' : ''}`}
            style={{ opacity }}
            onContextMenu={(e) =>
              handleContextMenuUI(e, setContextMenu, setOpacity, fadeOut)
            }
            onClick={(e) => {
              setShowUnReadMessages(false);
              os === 'iOS'
                ? handleContextMenuUI(e, setContextMenu, setOpacity, fadeOut)
                : !contextMenu?.show && setCurrentChat(conversation);
            }}
          >
            <div className="avatarWrapper">
              <img
                className={
                  !mobileView ? 'conversationImg' : 'conversationImgMobile'
                }
                src={
                  player?.profilePhoto ||
                  'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1656194537/Images/noAvatar_tsgpm9.png'
                }
                alt="Profile"
                draggable="false"
              />
              <span
                className={statusClass}
                title={derivedStatus}
                aria-label={`status ${derivedStatus}`}
              />
              {showUnReadMessages && (
                <span className="unReadMessagesIconBadge">
                  {Math.min(conversation.unReadMessages || 0, 99)}
                </span>
              )}
              {/* subtle activity ring when active */}
              {active && <span className="activeRing" aria-hidden="true" />}
            </div>

            <div className="conversationMeta">
              <div className="conversationRow">
                <span
                  className={
                    !mobileView ? 'conversationName' : 'conversationNameMobile'
                  }
                >
                  {player?.playerId || 'Loading...'}
                </span>
                {presenceEntry?.stale && (
                  <span
                    className="staleTag"
                    title="Stale presence (re-syncing)"
                  >
                    STALE
                  </span>
                )}
              </div>
              <div className="conversationRow secondaryLine">
                <span className="statusText">{statusTextDisplay}</span>
                {conversation.unReadMessages > 0 && !showUnReadMessages && (
                  <span className="miniUnread">
                    {conversation.unReadMessages}
                  </span>
                )}
              </div>
            </div>

            {contextMenu?.show && (
              <ContextMenu
                xPos={contextMenu.x}
                yPos={contextMenu.y}
                closeContextMenu={() => closeContextMenu(setContextMenu)}
                conversation={conversation}
                handleTooltipClose={() => handleTooltip(setOpen).close()}
                setMessages={setMessages}
                os={os}
                setCurrentChat={setCurrentChat}
                setShowUnReadMessages={setShowUnReadMessages}
                fwiend={player}
              />
            )}
          </div>
        </Tooltip>
      )}
    </div>
  );
}
